/**
 * Chart.js plugin that renders the legend as HTML into `#<containerID>` so it
 * can sit in the card footer next to the "View more insights" link. Used by
 * the Console's page-view chart (ASB-3567), rendering into the
 * `legend-wrapper` container the dashboard styles target.
 *
 * `itemHelp(text)` (ASB-4377, A.6 `help`) returns a legend entry's tooltip or
 * null; with `helpTooltipId`, an entry that has one gets the HoverTip's
 * markup on its text: `data-tooltip-*` for the react-tooltip the chart
 * renders under that id, a tab stop, and the help as its `aria-description`.
 * The click still toggles the series — the text is the entry — and so do
 * Enter and Space on it (`role="button"`, `aria-pressed` while the series is
 * shown); the list is rebuilt on every update, so the entry that had focus
 * takes it back. Without `helpTooltipId` (the Console) none of this applies.
 */
const getOrCreateLegendList = (chart, id) => {
	const legendContainer = document.getElementById(id);
	let listContainer = legendContainer.querySelector("ul");

	if (!listContainer) {
		listContainer = document.createElement("ul");

		legendContainer.appendChild(listContainer);
	}

	return listContainer;
};

export const htmlLegendPlugin = {
	id: "htmlLegend",
	afterUpdate(chart, args, options) {
		const ul = getOrCreateLegendList(chart, options.containerID);

		// A toggle from the keyboard (ASB-4377) runs this rebuild while its
		// entry has focus; the entry with the same text takes focus back below.
		const focusedText =
			options.helpTooltipId && ul.contains(document.activeElement)
				? document.activeElement.textContent
				: null;

		// Remove old legend items
		while (ul.firstChild) {
			ul.firstChild.remove();
		}

		// Reuse the built-in legendItems generator
		const items = chart.options.plugins.legend.labels.generateLabels(chart);

		items.reverse().forEach((item) => {
			const li = document.createElement("li");
			li.className = "legend-item";

			li.onclick = () => {
				const { type } = chart.config;
				if (type === "pie" || type === "doughnut") {
					// Pie and doughnut charts only have a single dataset and visibility is per item
					chart.toggleDataVisibility(item.index);
				} else {
					chart.setDatasetVisibility(
						item.datasetIndex,
						!chart.isDatasetVisible(item.datasetIndex)
					);
				}
				chart.update();
			};

			// Color box
			const boxSpan = document.createElement("span");
			boxSpan.style.background = item.fillStyle;
			boxSpan.style.borderColor = item.strokeStyle;
			boxSpan.style.borderWidth = item.lineWidth + "px";
			boxSpan.className = `legend-item--box ${item.lineJoin ?? false ? "line" : "box"}`;

			// Text
			const textContainer = document.createElement("p");
			textContainer.style.textDecoration = item.hidden ? "line-through" : "";
			textContainer.className = "legend-item--text p4 strong";

			const text = document.createTextNode(item.text);
			textContainer.appendChild(text);

			li.appendChild(boxSpan);
			li.appendChild(textContainer);

			const help = options.itemHelp ? options.itemHelp(item.text) : null;
			if (help && options.helpTooltipId) {
				textContainer.className += " as-hovertip legend-item--help";
				textContainer.setAttribute("data-tooltip-id", options.helpTooltipId);
				textContainer.setAttribute("data-tooltip-content", help);
				textContainer.setAttribute("tabindex", "0");
				textContainer.setAttribute("aria-description", help);
				// A tab stop that toggles the series is a button: Enter and Space
				// do what the click does (Space without scrolling the page), and
				// pressed means the series is shown.
				textContainer.setAttribute("role", "button");
				textContainer.setAttribute("aria-pressed", item.hidden ? "false" : "true");
				textContainer.onkeydown = (event) => {
					if (event.key !== "Enter" && event.key !== " ") return;
					if (event.key === " ") event.preventDefault();
					li.onclick();
				};
			}
			ul.appendChild(li);
		});

		if (focusedText !== null) {
			const entry = Array.from(ul.querySelectorAll(".legend-item--help")).find(
				(el) => el.textContent === focusedText
			);
			if (entry) entry.focus();
		}
	},
};
